import { useEffect, useState } from 'react';
import { Check, ShieldOff, ShieldCheck } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';

interface AgencyRow {
  id: string;
  name: string;
  slug: string;
  country: string | null;
  status: string;
  rating_avg: number | null;
  rating_count: number;
  created_at: string;
}

// Mirrors the backend AgencyStatus enum (pending → approved → suspended).
// There is no "rejected" state — the API 422s on any other value.
const STATUSES = ['pending', 'approved', 'suspended'] as const;

export function AgenciesPage() {
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('pending');
  const [rows, setRows] = useState<AgencyRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    api<{ data: AgencyRow[] }>(`/admin/agencies?status=${status}`)
      .then((res) => setRows(res.data))
      .catch(() => setError('Load failed'));
  }
  useEffect(load, [status]);

  async function action(id: string, route: string) {
    setBusy(id + route);
    try {
      await api(`/admin/agencies/${id}/${route}`, {
        method: 'POST',
        json: route === 'suspend' ? { reason: 'Admin action' } : {},
      });
      load();
    } catch {
      setError('Action failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-black tracking-tight">Agencies</h1>
        <p className="text-sm text-muted-foreground">Approval queue + suspend/restore.</p>
      </header>

      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <Button
            key={s}
            variant={status === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatus(s)}
          >
            {s}
          </Button>
        ))}
      </div>

      {error !== null && (
        <p
          role="alert"
          className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            {rows?.length ?? 0} agencies — {status}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rows === null && <div className="h-24 animate-pulse rounded-2xl bg-muted" />}
          {rows?.length === 0 && (
            <p className="text-sm text-muted-foreground">No agencies in this state.</p>
          )}
          {rows !== null && rows.length > 0 && (
            <ul className="space-y-2">
              {rows.map((a) => (
                <li
                  key={a.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border p-3"
                >
                  <div className="min-w-0">
                    <p className="font-bold">{a.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {a.country ?? '—'} · {a.slug}
                    </p>
                  </div>
                  <Badge variant="secondary">{a.status}</Badge>
                  <div className="flex gap-2">
                    {a.status === 'pending' && (
                      <Button
                        size="sm"
                        disabled={busy !== null}
                        onClick={() => void action(a.id, 'approve')}
                      >
                        <Check className="mr-1 h-4 w-4" /> Approve
                      </Button>
                    )}
                    {a.status === 'approved' && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy !== null}
                        onClick={() => void action(a.id, 'suspend')}
                      >
                        <ShieldOff className="mr-1 h-4 w-4" /> Suspend
                      </Button>
                    )}
                    {a.status === 'suspended' && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy !== null}
                        onClick={() => void action(a.id, 'restore')}
                      >
                        <ShieldCheck className="mr-1 h-4 w-4" /> Restore
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
